import React from "react"
import { Flex, Text } from "@chakra-ui/react"
import Icon from "components/elements/Icon"
import fitFontSize from "utilities/fitFontSize"
import type { KioskHomeLayout } from "../kioskLayout"

type Props = {
  accent: string
  label: string
  icon: string
  layout: KioskHomeLayout["buttons"]
  /** "scan" = filled accent; "register" = outlined white. */
  variant: "scan" | "register"
}

/** Circular action bubble. Diameter and insets come from the spec layout (px). */
const KioskPreviewHomeActionButton = ({
  accent, label, icon, layout, variant,
}: Props) => {
  const filled = variant === "scan"

  // Button titles auto-shrink to fit the circle's inner width, down to 50% (up to 2 lines).
  const availableWidthPx = layout.diameter - 2 * layout.hInset
  const labelFontPx = React.useMemo(
    () => fitFontSize({
      text: label,
      maxFontPx: layout.titleFontPx,
      availableWidthPx,
      minScale: 0.5,
      maxLines: 2,
      // Buttons are narrow: let labels wrap to 2 lines at full size, shrink only if needed.
      preferSingleLine: false,
      fontWeight: 500,
    }),
    [label, layout.titleFontPx, availableWidthPx],
  )

  return (
    <Flex
      direction="column"
      flexShrink={0}
      w={`${layout.diameter}px`}
      h={`${layout.diameter}px`}
      borderRadius="full"
      bg={filled ? accent : "rgba(255, 255, 255, 0.78)"}
      borderWidth={filled ? undefined : `${layout.strokePx}px`}
      borderColor={filled ? undefined : accent}
      color={filled ? "white" : accent}
      align="center"
      justify="center"
      pl={`${layout.hInset}px`}
      pr={`${layout.hInset}px`}
      pt={`${layout.vInset}px`}
      pb={`${layout.vInset}px`}
    >
      <Icon icon={icon} size={layout.iconPx / 4} mb={`${layout.iconTitleGap}px`} aria-hidden />
      <Text
        fontSize={`${labelFontPx}px`}
        fontWeight="medium"
        lineHeight={1.05}
        letterSpacing="tight"
        textAlign="center"
        noOfLines={2}
      >
        {label}
      </Text>
    </Flex>
  )
}

export default KioskPreviewHomeActionButton
